// ─────────────────────────────────────────────────────────────────────────────
// src/hooks/useFetch.js
//
// Generic data-fetching hook with loading + error states.
//
// Usage:
//   const { data, loading, error } = useFetch(ENDPOINTS.projects);
//
// Returns:
//   data    → parsed JSON (null until loaded)
//   loading → true while the request is in-flight
//   error   → error message string, or null on success
//
// Notes:
//   - Cancels the request if the component unmounts mid-flight (no memory leaks).
//   - Re-fetches automatically if the url changes.
//   - Pass null/undefined to skip fetching (returns { data: null, loading: false }).
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";

const useFetch = (url) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(!!url); // only true if we have a URL to fetch
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(`[useFetch] Failed to fetch ${url}:`, err.message);
          setError(err.message);
          setLoading(false);
        }
      });

    // Cleanup: cancel if component unmounts
    return () => {
      cancelled = true;
    };
  }, [url]);

  return { data, loading, error };
};

export default useFetch;
